/* oxlint-disable jsx-a11y/prefer-tag-over-role -- labelled region landmark on a div wrapper; a section would change the styled element */
'use client';

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  SyntheticEvent,
} from 'react';

import styled from '@emotion/styled';
import { cn } from 'cn';
import { useTranslations } from 'use-intl';

import { ReactPlayer } from '@/shared-utilities';

import Button from './ui.button';
import { BreakpointMarker } from './ui.video-player-breakpoint-marker';
import { useBreakpointRendering } from './ui.video-player-breakpoint.hooks';
import VideoPlayerControls from './ui.video-player-controls';
import { useIsDocumentVisible } from './ui.video-player-document-visibility.hooks';
import { VideoPlayerErrorOverlay } from './ui.video-player-error-overlay';
import { useIdleSuspendGuard } from './ui.video-player-idle-suspend.hooks';
import { useLoadingBackstop } from './ui.video-player-loading-backstop.hooks';
import { VideoPlayerLoadingOverlay } from './ui.video-player-loading-overlay';
import { useDelayedLoadingFlag } from './ui.video-player-loading.hooks';
import {
  useLoadFailureReport,
  usePlaybackSync,
} from './ui.video-player-playback.hooks';
import {
  BREAKPOINT_SIZE,
  COVER_TIMEOUT_MS,
  DEFAULT_VIDEO_PLAYER_CONFIG,
  IDLE_SUSPEND_GRACE_MS,
  LOADING_OVERLAY_DELAY_MS,
  MAX_LOADING_OVERLAY_MS,
  TIMELINE_HEIGHT,
  VIDEO_PLAYER_CROSS_ORIGIN,
} from './ui.video-player.consts';
import {
  isIdleWithoutData,
  isMediaReadyToRender,
  isSuspendedWithMetadata,
  reloadErroredMedia,
  resolveErrorOverlayVariant,
  resolveLoadingOverlayVariant,
  resolveMediaErrorCause,
  resolveMediaErrorState,
  resolveMediaIdentity,
  resolveMediaLoading,
  resolveReportedDuration,
  resolveSourceSwapState,
  resolveSeekTarget,
  resolveTaskNavigationEnabled,
  shouldPauseAtTrimEnd,
} from './ui.video-player.helpers';
import {
  useBreakpointManager,
  useFullscreenControls,
  useTimelineControls,
  useTrimPlayhead,
  useVideoPlayerEvents,
  useVideoPlayerState,
} from './ui.video-player.hooks';
import {
  Breakpoint,
  VideoPlayerErrorCause,
  VideoPlayerProps,
} from './ui.video-player.types';

const DEFAULT_BREAKPOINTS: Breakpoint[] = [];

const PlayerContainer = styled.div<{
  width: string | number;
  height: string | number;
}>`
  width: ${(props) => props.width};
  height: ${(props) => props.height};
`;

export default function VideoPlayer({
  src,
  className,
  width = DEFAULT_VIDEO_PLAYER_CONFIG.width,
  height = DEFAULT_VIDEO_PLAYER_CONFIG.height,
  controls = DEFAULT_VIDEO_PLAYER_CONFIG.controls,
  autoplay = DEFAULT_VIDEO_PLAYER_CONFIG.autoplay,
  muted = DEFAULT_VIDEO_PLAYER_CONFIG.muted,
  loop = DEFAULT_VIDEO_PLAYER_CONFIG.loop,
  breakpoints = DEFAULT_BREAKPOINTS,
  timelineHeight = TIMELINE_HEIGHT,
  breakpointSize = BREAKPOINT_SIZE,
  selectedBreakpointId,
  trimRange,
  onTrimRangeChange,
  onReady,
  onReadyToRenderChange,
  onError,
  onRetry,
  onTimeUpdate,
  onDurationChange,
  onPlay,
  onPause,
  onBreakpointReach,
  duration,
  isLoading,
  hideBottomControlsUntilPlaybackStarts = false,
  showTaskNavigation = false,
  ref,
  onClipStart,
}: VideoPlayerProps) {
  const t = useTranslations('VideoPlayer');
  const playerRef = useRef<ReactPlayer>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const retrySeekTimeRef = useRef<number | null>(null);
  const lastReadyToRenderRef = useRef<boolean | null>(null);

  const { state, updateState } = useVideoPlayerState({
    isPlaying: autoplay,
    isMuted: muted,
    duration: duration || 0,
  });

  const [mediaKey, setMediaKey] = useState(0);
  const [lastSrc, setLastSrc] = useState(src);
  const [hasEverPainted, setHasEverPainted] = useState(false);
  const [hasRenderedFrame, setHasRenderedFrame] = useState(false);
  const [isHoldReleased, setIsHoldReleased] = useState(false);
  const [hasPlaybackStarted, setHasPlaybackStarted] = useState(false);
  const [errorCause, setErrorCause] = useState<VideoPlayerErrorCause>('load');

  const isDocumentVisible = useIsDocumentVisible();

  usePlaybackSync(playerRef, state.isPlaying, () =>
    updateState({ isPlaying: false })
  );

  const reportLoadFailure = useLoadFailureReport(src, hasEverPainted);

  const sourceSwapState = resolveSourceSwapState(lastSrc, src);
  if (sourceSwapState) {
    setLastSrc(src);
    setHasRenderedFrame(false);
    if (resolveMediaIdentity(lastSrc) !== resolveMediaIdentity(src)) {
      setHasEverPainted(false);
    }
    setIsHoldReleased(false);
    updateState(sourceSwapState);
  }

  if (!isLoading && isHoldReleased) setIsHoldReleased(false);

  const isCovering =
    !state.hasError &&
    ((Boolean(isLoading) && !isHoldReleased) || !hasRenderedFrame);

  const isBuffering = useDelayedLoadingFlag(
    state.isLoading && !isCovering,
    LOADING_OVERLAY_DELAY_MS
  );

  const loadingOverlayVariant = resolveLoadingOverlayVariant({
    hasError: state.hasError,
    isCovering,
    isBuffering,
  });

  useLoadingBackstop(
    state.isLoading,
    src,
    () => updateState({ isLoading: false }),
    MAX_LOADING_OVERLAY_MS
  );

  useLoadingBackstop(
    isCovering && isDocumentVisible,
    `${mediaKey}:${src}`,
    () => {
      if (hasRenderedFrame) {
        setIsHoldReleased(true);
        return;
      }
      playerRef.current?.pause();
      setErrorCause('timeout');
      updateState({ hasError: true, isLoading: false, isPlaying: false });
      onError?.(new Error('Video load timed out'));
    },
    COVER_TIMEOUT_MS
  );

  const markReadyToRender = () => {
    retrySeekTimeRef.current = null;
    setHasRenderedFrame(true);
    setHasEverPainted(true);
    lastReadyToRenderRef.current = true;
    onReadyToRenderChange?.(true);
  };

  const scheduleIdleSuspendCheck = useIdleSuspendGuard(
    playerRef,
    () => {
      updateState({ isLoading: false });
      markReadyToRender();
    },
    IDLE_SUSPEND_GRACE_MS
  );

  const { checkBreakpointReach } = useBreakpointManager(
    breakpoints,
    onBreakpointReach
  );

  const reportReadyToRender = useCallback(
    (media: { readyState: number; seeking: boolean }) => {
      const isReady = isMediaReadyToRender(media);
      if (isReady) {
        setHasRenderedFrame(true);
        setHasEverPainted(true);
        retrySeekTimeRef.current = null;
      }
      if (lastReadyToRenderRef.current === isReady) return;
      lastReadyToRenderRef.current = isReady;
      onReadyToRenderChange?.(isReady);
    },
    [onReadyToRenderChange]
  );

  const seekToUnclamped = useCallback(
    (time: number) => {
      const targetTime = resolveSeekTarget(time, null);
      if (targetTime === null) return;

      if (playerRef.current) {
        playerRef.current.currentTime = targetTime;
      }
      updateState({ currentTime: targetTime });
    },
    [updateState]
  );

  const seekTo = useCallback(
    (time: number) => {
      const targetTime = resolveSeekTarget(time, trimRange);
      if (targetTime === null) return;

      seekToUnclamped(targetTime);
    },
    [trimRange, seekToUnclamped]
  );

  const isTrimMode = Boolean(trimRange && onTrimRangeChange);

  const {
    handleTimelineSeek,
    handleTrimRangeChange,
    releaseFreePlayhead,
    resolvePlayStart,
  } = useTrimPlayhead({
    trimRange,
    isTrimMode,
    duration,
    isPlaying: state.isPlaying,
    currentTime: state.currentTime,
    seekTo,
    seekToUnclamped,
    play: () => updateState({ isPlaying: true }),
    pause: () => {
      updateState({ isPlaying: false });
      onPause?.();
    },
    onTrimRangeChange,
  });

  const { timelineRef, handleTimelineClick } = useTimelineControls(
    state.duration,
    handleTimelineSeek
  );

  const { isFullscreen, toggleFullscreen } =
    useFullscreenControls(containerRef);

  const scrubGateRef = useRef(false);
  const wasPlayingRef = useRef(false);

  const handleScrubStart = () => {
    scrubGateRef.current = true;
    wasPlayingRef.current = state.isPlaying;
    if (state.isPlaying) {
      updateState({ isPlaying: false });
    }
  };

  const handleScrubEnd = () => {
    scrubGateRef.current = false;
    if (wasPlayingRef.current) {
      updateState({ isPlaying: true });
      wasPlayingRef.current = false;
    }
  };

  if (duration && duration !== state.duration) {
    updateState({ duration });
  }

  useEffect(() => {
    const handleSeekToTime = (event: Event) => {
      if (!('detail' in event)) return;
      const detail = event.detail;
      if (typeof detail !== 'object' || detail === null) return;
      const time = 'time' in detail ? detail.time : undefined;
      if (typeof time === 'number' && isFinite(time) && time >= 0) {
        if (playerRef.current) {
          playerRef.current.currentTime = time;
          reportReadyToRender(playerRef.current);
        }
        updateState(
          'autoPlay' in detail && detail.autoPlay === true
            ? { currentTime: time, isPlaying: true }
            : { currentTime: time }
        );
      }
    };

    window.addEventListener('seekToTime', handleSeekToTime);

    return () => {
      window.removeEventListener('seekToTime', handleSeekToTime);
    };
  }, [updateState, reportReadyToRender]);

  const handleMediaLoadingEvent = (event: SyntheticEvent<HTMLVideoElement>) => {
    const nextIsLoading = resolveMediaLoading(event.type);
    if (nextIsLoading !== null) updateState({ isLoading: nextIsLoading });
    reportReadyToRender(event.currentTarget);
    if (
      state.hasError &&
      errorCause === 'timeout' &&
      isMediaReadyToRender(event.currentTarget)
    ) {
      updateState({ hasError: false, errorMessage: '' });
    }
  };

  const handleLoadedMetadata = (event: SyntheticEvent<HTMLVideoElement>) => {
    const retrySeekTime = retrySeekTimeRef.current;
    if (retrySeekTime !== null) event.currentTarget.currentTime = retrySeekTime;
    handleMediaLoadingEvent(event);
  };

  const handleMediaSuspend = (event: SyntheticEvent<HTMLVideoElement>) => {
    const media = event.currentTarget;
    if (isIdleWithoutData(media)) {
      scheduleIdleSuspendCheck();
      return;
    }
    if (isCovering && isSuspendedWithMetadata(media)) {
      markReadyToRender();
    }
  };

  const handlePlayerReady = () => {
    if (isCovering && !(playerRef.current instanceof HTMLMediaElement)) {
      markReadyToRender();
    }
    handleReady();
  };

  const handleRetry = () => {
    retrySeekTimeRef.current = state.currentTime > 0 ? state.currentTime : null;
    setHasRenderedFrame(false);
    setMediaKey((key) => key + 1);
    setIsHoldReleased(false);
    updateState({
      hasError: false,
      errorMessage: '',
      isLoading: true,
      isPlaying: false,
    });
    onRetry?.();
  };

  const callbacks = {
    onPlay: () => {
      updateState({ isPlaying: true });
      setHasPlaybackStarted(true);
      onPlay?.();
    },
    onPause: () => {
      updateState({ isPlaying: false });
      onPause?.();
    },
    onTimeUpdate: (currentTime: number) => {
      const pauseVideo = () => {
        playerRef.current?.pause();
        updateState({ isPlaying: false });
        onPause?.();
      };

      if (
        trimRange &&
        state.isPlaying &&
        shouldPauseAtTrimEnd(state.currentTime, currentTime, trimRange)
      ) {
        releaseFreePlayhead();
        updateState({ currentTime: trimRange.endTime });
        onTimeUpdate?.(trimRange.endTime);
        pauseVideo();
        return;
      }
      if (duration && currentTime >= duration && state.isPlaying) {
        updateState({ currentTime: duration });
        onTimeUpdate?.(duration);
        pauseVideo();
        return;
      }
      updateState({ currentTime });
      onTimeUpdate?.(currentTime);
    },
    onDurationChange: (duration: number) => {
      const finalDuration = resolveReportedDuration(duration, state.duration);
      updateState({ duration: finalDuration });
      onDurationChange?.(finalDuration);
    },
    onError: (error: Error) => {
      const showsErrorPanel = isCovering || state.currentTime === 0;
      if (showsErrorPanel) {
        setErrorCause(resolveMediaErrorCause(hasEverPainted));
      }
      updateState(resolveMediaErrorState(showsErrorPanel, error.message));
      onError?.(error);
    },
    onReady: () => {
      updateState({ hasError: false });
      onReady?.();
    },
    onBreakpointReach,
  };

  const scrubGatedCallbacks = {
    ...callbacks,
    onTimeUpdate: (currentTime: number) => {
      if (scrubGateRef.current) return;
      callbacks.onTimeUpdate(currentTime);
    },
  };
  const checkBreakpointReachGuarded = (currentTime: number) => {
    if (scrubGateRef.current) return;
    checkBreakpointReach(currentTime);
  };

  const {
    handlePlay,
    handlePause,
    handleProgress,
    handleDuration,
    handleError,
    handleReady,
    handleStart,
  } = useVideoPlayerEvents(scrubGatedCallbacks, checkBreakpointReachGuarded);

  const togglePlayPause = useCallback(() => {
    if (state.isPlaying) {
      handlePause();
    } else {
      const startTime = resolvePlayStart(Boolean(playerRef.current?.ended));
      reloadErroredMedia(playerRef.current);
      if (startTime !== null) {
        seekToUnclamped(startTime);
      }
      handlePlay();
    }
  }, [
    state.isPlaying,
    handlePlay,
    handlePause,
    seekToUnclamped,
    resolvePlayStart,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      togglePlayPause,
    }),
    [togglePlayPause]
  );

  const toggleMute = () => {
    updateState({ isMuted: !state.isMuted });
  };

  const rewind10s = () => {
    const target = trimRange
      ? Math.max(trimRange.startTime, state.currentTime - 10)
      : Math.max(0, state.currentTime - 10);
    seekTo(target);
  };

  const forward10s = () => {
    const target = trimRange
      ? Math.min(trimRange.endTime, state.currentTime + 10)
      : Math.min(state.duration, state.currentTime + 10);
    seekTo(target);
  };

  const { positions } = useBreakpointRendering(
    selectedBreakpointId || null,
    breakpoints,
    state.duration
  );

  const taskNavigationEnabled = resolveTaskNavigationEnabled(
    showTaskNavigation,
    isTrimMode
  );

  const renderBreakpoints = taskNavigationEnabled
    ? null
    : breakpoints.map((breakpoint, index) => {
        const positionData = positions[index];
        if (!positionData) return null;

        return (
          <BreakpointMarker
            key={breakpoint.id}
            breakpoint={breakpoint}
            position={positionData.position}
            size={breakpointSize}
            isInHighlight={positionData.isInHighlight}
          />
        );
      });

  const showBottomControls =
    controls && (!hideBottomControlsUntilPlaybackStarts || hasPlaybackStarted);

  return (
    <PlayerContainer
      data-test='video-player'
      ref={containerRef}
      className={cn('flex size-full flex-col', className)}
      width={width}
      height={height}
      role='region'
      aria-label={t('videoPlayerAria')}
    >
      <div className='relative min-h-0 w-full flex-1 overflow-hidden'>
        <ReactPlayer
          key={mediaKey}
          ref={playerRef}
          src={src}
          volume={state.volume}
          muted={state.isMuted}
          width='100%'
          loop={loop}
          height='100%'
          crossOrigin={VIDEO_PLAYER_CROSS_ORIGIN}
          playsInline
          onPlay={handlePlay}
          onPause={handlePause}
          onTimeUpdate={(event) => {
            const target = event.currentTarget;
            handleProgress({
              playedSeconds: target.currentTime,
              played:
                target.duration > 0 ? target.currentTime / target.duration : 0,
            });
          }}
          onDurationChange={(event) =>
            handleDuration(event.currentTarget.duration)
          }
          onError={(event) => {
            const mediaError = event.currentTarget.error;
            reportLoadFailure(mediaError);
            handleError(
              new Error(mediaError?.message || 'Video playback failed')
            );
          }}
          onReady={handlePlayerReady}
          onStart={handleStart}
          onLoadStart={handleMediaLoadingEvent}
          onLoadedMetadata={handleLoadedMetadata}
          onLoadedData={handleMediaLoadingEvent}
          onCanPlay={handleMediaLoadingEvent}
          onSeeking={handleMediaLoadingEvent}
          onSeeked={handleMediaLoadingEvent}
          onPlaying={handleMediaLoadingEvent}
          onWaiting={handleMediaLoadingEvent}
          onSuspend={handleMediaSuspend}
          controls={false}
        />

        <Button
          type='button'
          onClick={togglePlayPause}
          inert={state.hasError}
          data-playing={state.isPlaying}
          className='group absolute inset-0 flex size-full rounded-none bg-black/40 p-0 hover:enabled:bg-black/40 data-[playing=true]:bg-transparent data-[playing=true]:hover:enabled:bg-transparent [&_svg]:size-auto'
          aria-label={state.isPlaying ? t('pauseAria') : t('playAria')}
          data-test='video-player-toggle'
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='69'
            height='80'
            viewBox='0 0 69 80'
            fill='none'
            aria-hidden='true'
            className='group-data-[playing=true]:hidden'
          >
            <path
              d='M13.1172 1.26008C10.4578 -0.364637 7.11562 -0.418199 4.40234 1.09939C1.68906 2.61698 0 5.47362 0 8.58021V71.4262C0 74.5328 1.68906 77.3895 4.40234 78.9071C7.11562 80.4247 10.4578 80.3532 13.1172 78.7464L64.8672 47.3234C67.4367 45.7701 69 43.0027 69 40.0032C69 37.0038 67.4367 34.2542 64.8672 32.6831L13.1172 1.26008Z'
              fill='white'
            />
          </svg>
        </Button>

        {loadingOverlayVariant ? (
          <VideoPlayerLoadingOverlay variant={loadingOverlayVariant} />
        ) : null}

        {state.hasError ? (
          <VideoPlayerErrorOverlay
            variant={resolveErrorOverlayVariant(errorCause)}
            onRetry={handleRetry}
          />
        ) : null}
      </div>

      {showBottomControls ? (
        <VideoPlayerControls
          timelineRef={timelineRef}
          timelineHeight={timelineHeight}
          handleTimelineClick={handleTimelineClick}
          seekTo={seekTo}
          state={state}
          renderBreakpoints={renderBreakpoints}
          togglePlayPause={togglePlayPause}
          rewind10s={rewind10s}
          forward10s={forward10s}
          toggleMute={toggleMute}
          toggleFullscreen={toggleFullscreen}
          isFullscreen={isFullscreen}
          selectedBreakpointId={selectedBreakpointId}
          breakpoints={breakpoints}
          trimRange={trimRange}
          onTrimRangeChange={
            onTrimRangeChange ? handleTrimRangeChange : undefined
          }
          onClipStart={onClipStart}
          onScrubStart={handleScrubStart}
          onScrubEnd={handleScrubEnd}
          showTaskNavigation={taskNavigationEnabled}
          disabled={state.hasError}
        />
      ) : null}
    </PlayerContainer>
  );
}
